/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from "@emotion/styled";
import { TableCell, TableRow } from "@mui/material";

export const SectionTitle = styled("div")`
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  color: ${({ theme }) => (theme as any).colors.primaryMedium};
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => (theme as any).colors.border};

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    padding: 12px 30px;
  }
`;

export const Card = styled("div")`
  background: #ffffff;
  box-shadow: ${({ theme }) => (theme as any).shadow.card};
  border-radius: 8px;
`;

export const MetricsGrid = styled("div")`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;

  ${({ theme }) => (theme as any).breakpoints.up("sm")} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const MetricsContainerCard = styled(Card)`
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    padding: 20px 24px;
    gap: 20px;
  }
`;

export const MetricsContainerHeader = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const MetricsContainerTitle = styled("div")`
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  color: ${({ theme }) => (theme as any).colors.primaryMedium};
`;

export const MetricsInnerGrid = styled("div")`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  /* Stack Proposals + Booked Trips until desktop — matches the mobile shell
     breakpoint (md = 900px) so tablet widths get full-width metric cards. */
  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

// Inner card for one metric group ("Proposals", "Booked Trips"). Visual
// language mirrors the pipeline-section card: white background, hairline
// border, blue uppercase header strip with a bottom divider. Padding lives
// on the header + body sub-components so the strip extends edge-to-edge.
export const MetricSubCard = styled("div")`
  background: #ffffff;
  border: 1px solid ${({ theme }) => (theme as any).colors.border};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

export const MetricSubCardHeader = styled("div")`
  padding: 10px 16px 8px;
  background: ${({ theme }) => (theme as any).colors.canvasDeep};
  border-bottom: 1px solid ${({ theme }) => (theme as any).colors.grayLight};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

// One row of equal-width tiles below md — flex: 1 1 0 + min-width: 0 keeps the
// row inside the card (no horizontal scroll). Labels wrap; values stay nowrap.
export const MetricSubCardBody = styled("div")`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 4px;
  padding: 10px 8px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    gap: 4px;
    padding: 12px 8px;
  }
`;

// Single metric "card" — label on top, value (+ optional delta chip) on a
// row beneath. Visual structure mirrors StepCardButton so a row of cells
// looks like a row of pipeline cards.
export const MetricCell = styled("div")`
  flex: 1 1 0;
  min-width: 0;
  padding: 6px 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    padding: 6px 10px;
    gap: 8px;
  }
`;

export const MetricCellLabel = styled("div")`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => (theme as any).colors.textSecondary};
  line-height: 1.25;
  overflow-wrap: anywhere;
  hyphens: auto;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    font-size: 12px;
    line-height: 1.3;
  }
`;

export const MetricCellValueRow = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
`;

export const MetricCellValue = styled("div")`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => (theme as any).colors.primaryMedium};
  line-height: 1;
  /* Keep the formatted number on a single line — labels can wrap, but a
     wrapped "$245,000" reads as broken. */
  white-space: nowrap;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    font-size: 22px;
  }
`;

export const MetricDeltaChip = styled("span")<{ direction: "up" | "down" | "flat" }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 4px;
  border-radius: 999px;
  max-width: 100%;
  color: ${({ direction, theme }) =>
    direction === "up"
      ? (theme as any).colors.success
      : direction === "down"
        ? (theme as any).colors.danger
        : (theme as any).colors.textSecondary};
  background: ${({ direction }) =>
    direction === "up"
      ? "rgba(27, 122, 58, 0.1)"
      : direction === "down"
      ? "rgba(163, 38, 26, 0.1)"
      : "rgba(106, 106, 106, 0.1)"};

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    font-size: 11px;
    padding: 2px 6px;
  }
`;

export const MetricGroupCard = styled(Card)`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const MetricGroupTitle = styled("div")`
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => (theme as any).colors.primaryMedium};
`;

// The dashboard list renders as a real <table> (same approach as the trip
// list — see Trips/TripRow.tsx). The table layout algorithm resolves one
// width per column across every row, so a long status label or a row
// carrying two chips widens the Status column for the whole table instead of
// squeezing that row's other cells out of alignment.
export const TableScroll = styled("div")`
  overflow-x: auto;
`;

export const HeadCell = styled(TableCell)`
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
  color: ${({ theme }) => (theme as any).colors.textMuted};
  border-bottom: 1px solid ${({ theme }) => (theme as any).colors.grayLight};
`;

export const BodyRow = styled(TableRow)`
  &:last-of-type td {
    border-bottom: none;
  }
`;

export const BodyCell = styled(TableCell)`
  padding: 12px 16px;
  font-size: 13px;
  vertical-align: middle;
  color: ${({ theme }) => (theme as any).colors.grayDarker};
  border-bottom: 1px solid ${({ theme }) => (theme as any).colors.grayLight};
`;

// `width: 1%` makes these two shrink-to-fit under the table layout algorithm,
// so the flexible text columns keep the remaining width.
export const StatusCell = styled(BodyCell)`
  width: 1%;
  white-space: nowrap;
`;

export const ActionCell = styled(BodyCell)`
  width: 1%;
  white-space: nowrap;
  padding-left: 0;
  text-align: right;
`;

// Status chips sit on one line and stay right-aligned against the chevron.
export const StatusChips = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const ColumnRowTitle = styled("div")`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => (theme as any).colors.grayDarker};
`;

export const ColumnRowLegCount = styled("span")`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => (theme as any).colors.textSecondary};
`;

// Text cell contents. The max-width keeps an unusually long customer name
// from stealing width from the other columns — the full value stays
// available through the title attribute.
export const CellValue = styled("div")`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
`;

// Mobile row: the per-field label that replaces the desktop table header.
export const MobileFieldLabel = styled("div")`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ theme }) => (theme as any).colors.textMuted};
`;

export const MobileField = styled("div")`
  margin-bottom: 8px;
  font-size: 14px;
  color: ${({ theme }) => (theme as any).colors.grayDarker};
`;

export const MobileRowLayout = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 4px 0;
`;

// Chips wrap on mobile — vertical growth is cheap there, horizontal isn't.
export const MobileStatusChips = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

export const EmptyState = styled("div")`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => (theme as any).colors.textSecondary};
  font-size: 14px;
`;

export const TimelineCard = styled(Card)`
  padding: 16px;
  margin-bottom: 24px;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    padding: 20px 24px;
  }
`;

export const TimelineHeader = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 16px;
`;

export const TimelineTitle = styled("div")`
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  color: ${({ theme }) => (theme as any).colors.primaryMedium};
`;

// Lays out the four pipeline sections. Desktop: row with right-pointing
// chevron icons between each section. Small viewports: column with
// downward-pointing chevrons between each section so the directional
// pipeline metaphor still reads top-to-bottom.
export const TimelineSectionsTrack = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    flex-direction: row;
    align-items: stretch;
    gap: 4px;
  }
`;

// A section is a plain white card matching the existing operator-app card
// style (1px hairline border, 8px radius, no shadow — the parent
// MetricsContainerCard / TimelineCard already provides the lift). On
// desktop sections grow proportionally to their card count so multi-card
// sections (Pending) don't squeeze their cards while single-card sections
// (Requests, Booked) stay compact. On mobile they auto-size to content so
// nothing collapses to zero height when there's no parent height to
// distribute via flex-grow.
export const TimelineSection = styled("div")<{ cardCount: number }>`
  background: #ffffff;
  border: 1px solid ${({ theme }) => (theme as any).colors.border};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  /* flex-basis auto so the section sizes to its own content on mobile —
     pairing flex-grow with a column-flex parent that has no definite
     height collapsed sections to 0 in the previous iteration. */
  flex: 0 1 auto;
  min-width: 0;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    flex: ${({ cardCount }) => cardCount} 1 0;
  }
`;

// Subtle per-section header background tint. Picked for visual separation
// only — the title text stays in the operator-app primary blue so the
// overall palette stays small.
const SECTION_HEADER_BG = {
  blue: "#e7f0fa",
  amber: "#fbecd2",
  purple: "#efe7fa",
  green: "#e3f1e7",
} as const;

type SectionColorTheme = keyof typeof SECTION_HEADER_BG;

export const TimelineSectionHeader = styled("div")<{
  colorTheme: SectionColorTheme;
}>`
  padding: 10px 16px 8px;
  background: ${({ colorTheme }) => SECTION_HEADER_BG[colorTheme]};
  border-bottom: 1px solid ${({ theme }) => (theme as any).colors.grayLight};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const TimelineSectionTitle = styled("div")`
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => (theme as any).colors.primaryMedium};
`;

// Equal-width tiles in one row — no horizontal scroll; long labels wrap.
export const TimelineSectionCards = styled("div")`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 4px;
  padding: 8px 8px 10px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    padding: 12px 8px;
  }
`;

export const StepCardButton = styled("button")`
  appearance: none;
  border: none;
  background: transparent;
  padding: 6px 4px;
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 0;
  min-width: 0;
  border-radius: 6px;
  transition: background-color 120ms ease;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    padding: 6px 10px;
    gap: 8px;
  }

  &:hover {
    background-color: ${({ theme }) => (theme as any).colors.canvasDeep};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => (theme as any).colors.primaryMedium};
    outline-offset: 2px;
  }
`;

export const StepCardLabel = styled("div")`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => (theme as any).colors.textSecondary};
  line-height: 1.25;
  overflow-wrap: anywhere;
  hyphens: auto;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    font-size: 12px;
    line-height: 1.3;
  }
`;

export const StepCardCount = styled("div")`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => (theme as any).colors.primaryMedium};
  line-height: 1;

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    font-size: 24px;
  }
`;

// Chevron between sections. Renders pointing right on desktop and rotates
// 90° to point down on small viewports so the pipeline reads top-to-bottom.
export const TimelineSectionChevron = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => (theme as any).colors.grayDark};
  flex: 0 0 auto;
  align-self: center;

  & svg {
    width: 20px;
    height: 20px;
    transform: rotate(90deg);
  }

  ${({ theme }) => (theme as any).breakpoints.up("md")} {
    padding: 0 2px;

    & svg {
      transform: none;
      width: 22px;
      height: 22px;
    }
  }
`;

export const SectionInfoIconWrap = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => (theme as any).colors.textMuted};
  cursor: help;
  transition: color 120ms ease;

  & svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${({ theme }) => (theme as any).colors.primaryMedium};
  }
`;
